import * as yup from 'yup';
import {
  assertionOptions,
  classificationOptions,
  controlNatureOptions,
  controlTypeOptions,
  frequencyOptions,
  significanceOptions,
} from '../../utils/controlConstants.js';

const controlTypeValues = controlTypeOptions.map((o) => o.value);
const frequencyValues = [...frequencyOptions, 'event-driven', 'event based'];
const significanceValues = significanceOptions.map((o) => o.value);
const classificationValues = classificationOptions.map((o) => o.value);
const controlNatureValues = controlNatureOptions.map((o) => o.value);
const assertionValues = assertionOptions.map((o) => o.value);

/** @deprecated Configure-test readiness still references a minimum text length in functional.ts */
export const CONTROL_TEXT_MIN_LENGTH = 20;

/** Keep `''` in form state — RHF + yupResolver revert `undefined` to defaultValues on clear. */
const optionalEnum = <T extends string>(values: T[], label: string) =>
  yup
    .string()
    .trim()
    .test('optional-enum', `Please select a valid ${label}`, (value) => {
      if (value == null || value === '') return true;
      return values.includes(value as T);
    });

export const controlFormSchema = yup
  .object({
    controlId: yup
      .string()
      .trim()
      .required('Client control id is required')
      .max(255, 'Client referral ID cannot be more than 255 characters'),
    title: yup
      .string()
      .trim()
      .required('Title is required')
      .max(255, 'Title cannot be more than 255 characters'),
    objective: yup.string().trim(),
    controlWordingText: yup
      .string()
      .trim()
      .required('Control wording is required'),
    controlType: optionalEnum(controlTypeValues, 'type'),
    frequency: optionalEnum(frequencyValues, 'frequency'),
    significance: optionalEnum(significanceValues, 'significance'),
    classification: optionalEnum(classificationValues, 'classification'),
    controlNature: optionalEnum(controlNatureValues, 'control nature'),
    mrcControl: yup.boolean().default(false),
    assertions: yup
      .array()
      .of(yup.string().oneOf(assertionValues).required())
      .default([]),
    department: yup.string().trim().max(255, 'Department cannot be more than 255 characters'),
    controlOwner: yup.string().trim().max(255, 'Control owner cannot be more than 255 characters'),
    keyReportIds: yup.array().of(yup.string().trim().required()).default([]),
    externalAuditorReliance: yup.boolean().nullable().default(false),
    itgcSystemIds: yup
      .array()
      .of(yup.string().trim().required())
      .default([])
      .when('classification', {
        is: 'itgc',
        then: (schema) => schema.min(1, 'Select at least one system.'),
        otherwise: (schema) => schema,
      }),
    itgcDomainId: yup
      .string()
      .trim()
      .default('')
      .when('classification', {
        is: 'itgc',
        then: (schema) => schema.required('Domain is required.'),
        otherwise: (schema) => schema,
      }),
  })
  .required();

/** Control detail edit — same rules as create; configure-test readiness is checked separately. */
export const controlConfigureTestSchema = controlFormSchema;

export type ControlFormValues = yup.InferType<typeof controlFormSchema>;

export const controlRiskAssociationSchema = yup
  .object({
    riskId: yup.string().trim().required(),
    mitigatingProcessIds: yup
      .array()
      .of(yup.string().trim().required())
      .min(1, 'Assign at least one mapped process for this risk.')
      .required(),
  })
  .required();

export const controlRiskAssociationsFormSchema = yup
  .object({
    riskAssociations: yup.array().of(controlRiskAssociationSchema).default([]),
  })
  .required();

export type ControlRiskAssociationsFormValues = yup.InferType<typeof controlRiskAssociationsFormSchema>;

export const defaultControlFormValues: ControlFormValues = {
  controlId: '',
  title: '',
  objective: '',
  controlWordingText: '',
  controlType: 'manual',
  frequency: '',
  significance: 'non-key',
  classification: 'business',
  controlNature: '',
  mrcControl: false,
  assertions: [],
  department: '',
  controlOwner: '',
  keyReportIds: [],
  externalAuditorReliance: false,
  itgcSystemIds: [],
  itgcDomainId: '',
};
